import createReducer from '../../../../../../utils/createReducer';
import { uniqueId } from '@comeon/utility';
import { ACTION_PREFIX } from '../../../sportsbook-constants';
import type { RootState, ThunkAction } from '~/store/types';
import selectSportsbookState from '../../../sportsbook-state-selector';
import { selectItems } from './betslip-data-duck';
import {
	placeBets as apiPlaceBets,
	subscribeToPlacementUpdates,
	unsubscribeFromPlacementUpdates
} from '../../../api/betting-service-api';
import {
	CheckBetsResponseBet,
	PlaceBetsResponseBet,
	PlaceBetsResponseBetStatus
} from '../../../api/betting-service/betting-service-types';
import {
	BetslipBet,
	BetslipBetWithData,
	BetslipDataItem,
	BetPlacementStatus,
	BetslipPersistedBet,
	BetBonusType,
	BetslipBetErrorType
} from '../sportsbook-betslip-types';
import { isBetWithCompleteData } from '../sportsbook-betslip-utils';

import {
	bettingApiErrorToBetslipBetError,
	calculateBetWithdrawStake,
	calculateBetFinalOdds,
	createOrUpdateComboBet,
	createOrUpdateSingleBets,
	createOrUpdateSystemBets,
	replacePersistedBets,
	bettingApiWarningToBetslipBetWarning
} from './betslip-duck-utils';
import { isUnavailableError } from '../sportsbook-betslip-error-utils';
import { BetType } from '../../../sportsbook-types';
import createDebouncedCheckBets, { isCancelledError } from './betslip-betting-debounced-checkbets';
import { createSelector } from 'reselect';
import { reloadBalance } from './betslip-common-duck';
import normalizeUnifiedId from '../../../utils/normalize-unified-id';
import { selectSportsbookUnifiedFeedEnabled } from '~/components/brand/ducks/selectors';

const SUBSCRIPTION_REFERENCE = 'betslip_betting_duck';

type State = {
	bets: Array<BetslipBet>;
};

const initialState: State = { bets: [] };

// Selectors
export const selectState = (state: RootState) => selectSportsbookState(state).betslip.betting;
export const selectBets = (state: RootState) => selectState(state).bets;

export const selectBetsWithData = createSelector([selectBets, selectItems], (bets, items) => {
	return bets.map(bet => ({
		...bet,
		items: bet.items.map(betItem => {
			const item = items.find(dataItem => dataItem.ref === betItem.ref) as BetslipDataItem;
			return {
				...betItem,
				...item
			};
		})
	}));
});

// Actions
const BETS_REPLACED = `${ACTION_PREFIX}BETSLIP/BETTING/BETS_REPLACED`;
type BetsReplacedAction = {
	type: typeof BETS_REPLACED;
	payload: { bets: Array<BetslipBet> };
};

const STAKE_CHANGED = `${ACTION_PREFIX}BETSLIP/BETTING/STAKE_CHANGED`;
type StakeChangedAction = {
	type: typeof STAKE_CHANGED;
	payload: { ref: string; stake: number };
};

const TOGGLE_ACCEPT_ALL_ODDS_CHANGES = `${ACTION_PREFIX}BETSLIP/BETTING/TOGGLE_ACCEPT_ALL_ODDS_CHANGES`;

const SELECTED_BONUS_ID_CHANGED = `${ACTION_PREFIX}BETSLIP/BETTING/SELECTED_BONUS_ID_CHANGED`;
type SelectedBonusIdChangedAction = {
	type: typeof SELECTED_BONUS_ID_CHANGED;
	payload: { ref: string; bonusId: number | undefined };
};

export const BETS_CHECKED = `${ACTION_PREFIX}BETSLIP/BETTING/BETS_CHECKED` as const;
export type BetsCheckedAction = {
	type: typeof BETS_CHECKED;
	payload: { bets: Array<CheckBetsResponseBet> };
};

export const BETS_PLACEMENT_STARTED = `${ACTION_PREFIX}BETSLIP/BETTING/BETS_PLACEMENT_STARTED` as const;
export type BetsPlacementStartedAction = {
	type: typeof BETS_PLACEMENT_STARTED;
	payload: { betRefs: Array<string>; betType: BetType; totalStake: number };
};

export const BETS_PLACEMENT_UPDATE_RECEIVED = `${ACTION_PREFIX}BETSLIP/BETTING/BETS_PLACEMENT_UPDATE_RECEIVED` as const;
export type BetsPlacementUpdateReceivedAction = {
	type: typeof BETS_PLACEMENT_UPDATE_RECEIVED;
	payload: { bets: Array<PlaceBetsResponseBet> };
};

const BETS_PLACEMENT_FAILED = `${ACTION_PREFIX}BETSLIP/BETTING/BETS_PLACEMENT_FAILED`;
type BetsPlacementFailedAction = {
	type: typeof BETS_PLACEMENT_FAILED;
	payload: { betRefs: Array<string> };
};

const BETS_PLACEMENT_UNKNOWN = `${ACTION_PREFIX}BETSLIP/BETTING/BETS_PLACEMENT_UNKNOWN`;
type BetsPlacementUnknownAction = {
	type: typeof BETS_PLACEMENT_UNKNOWN;
	payload: { betRefs: Array<string> };
};

const BETS_CHANGES_ACCEPTED = `${ACTION_PREFIX}BETSLIP/BETTING/BETS_CHANGES_ACCEPTED`;

export const NEW_OFFER_ACCEPTED = `${ACTION_PREFIX}BETSLIP/BETTING/NEW_OFFER_ACCEPTED` as const;
export type NewOfferAcceptedAction = {
	type: typeof NEW_OFFER_ACCEPTED;
	payload: { betRefs: Array<string>; newBetRefs: Array<string> };
};

export const NEW_OFFER_REJECTED = `${ACTION_PREFIX}BETSLIP/BETTING/NEW_OFFER_REJECTED` as const;
export type NewOfferRejectedAction = {
	type: typeof NEW_OFFER_REJECTED;
	payload: { betRefs: Array<string> };
};

const DISPOSED = `${ACTION_PREFIX}BETSLIP/BETTING/DISPOSED`;
type DisposedAction = {
	type: typeof DISPOSED;
};

// ActionCreators
export const refreshBets = (): ThunkAction<BetsReplacedAction> => (dispatch, getState) => {
	const currentBets = selectBets(getState());
	const items = selectItems(getState());

	// Singles
	let bets = [...createOrUpdateSingleBets(currentBets, items)];

	// Combo
	if (items.length >= 2) {
		bets.push(createOrUpdateComboBet(currentBets, items));
	}

	// Systems
	if (items.length >= 3) {
		bets = [...bets, ...createOrUpdateSystemBets(currentBets, items)];
	}

	dispatch({
		type: BETS_REPLACED,
		payload: { bets }
	});
};

// This is a violent actionCreator, think twice before using it and ALWAYS refreshBets after.
// Now used only with persisted bets.
export const replaceBets = (bets: Array<BetslipPersistedBet>): BetsReplacedAction => ({
	type: BETS_REPLACED,
	payload: {
		bets: replacePersistedBets(bets)
	}
});

export const changeStake = (ref: string, stake: number): StakeChangedAction => ({
	type: STAKE_CHANGED,
	payload: { ref, stake }
});

export const toggleAcceptAllOddsChanges = () => ({
	type: TOGGLE_ACCEPT_ALL_ODDS_CHANGES
});

export const changeSelectedBonusId = (ref: string, bonusId: number | undefined): SelectedBonusIdChangedAction => ({
	type: SELECTED_BONUS_ID_CHANGED,
	payload: { ref, bonusId }
});

const debouncedCheckBets = createDebouncedCheckBets();

const normalizeIdsInCheckBetResponse = (
	bets: Array<CheckBetsResponseBet>,
	isUnifiedFeedEnabled: boolean
): Array<CheckBetsResponseBet> => {
	if (!isUnifiedFeedEnabled) {
		return bets;
	}

	let normalizedBets = bets;

	if (normalizedBets.some(b => b.errors?.some(e => e.selectionIds))) {
		normalizedBets = normalizedBets.map(bet => ({
			...bet,
			errors: bet.errors.map(error => ({
				...error,
				selectionIds: error.selectionIds?.map(normalizeUnifiedId)
			}))
		}));
	}

	if (normalizedBets.some(b => b.warnings?.some(e => e.selectionIds))) {
		normalizedBets = normalizedBets.map(bet => ({
			...bet,
			warnings: bet.warnings.map(warning => ({
				...warning,
				selectionIds: warning.selectionIds?.map(normalizeUnifiedId)
			}))
		}));
	}

	return normalizedBets;
};

export const checkBets =
	(bets: Array<BetslipBetWithData>, postCheckBetsCallback: () => void): ThunkAction<BetsCheckedAction> =>
	async (dispatch, getState) => {
		const betsToCheck = bets.filter(isBetWithCompleteData);
		if (!betsToCheck.length) {
			return;
		}

		try {
			const response = await debouncedCheckBets({
				bets: betsToCheck.map(bet => {
					const { selectedBonus } = bet;
					return {
						ref: bet.ref,
						type: bet.type,
						stake: calculateBetWithdrawStake(bet),
						items: bet.items.map(item => ({
							eventId: item.event.id,
							marketId: item.market.id,
							marketTypeId: item.market.marketType.externalId,
							selectionId: item.selection.id,
							sportId: item.event.externalSportId,
							leagueId: item.event.leagueId,
							odds: item.acceptedOdds,
							points: item.selection.points !== 0 ? item.selection.points : undefined,
							isLive: item.event.isLive
						})),
						combination: bet.combination,
						givenBonusTokenId: selectedBonus?.givenBonusTokenId,
						isFreeBet: selectedBonus?.bonusType === BetBonusType.FREE_BET
					};
				})
			});

			const isUnifiedFeedEnabled = selectSportsbookUnifiedFeedEnabled(getState());
			const normalizedBets = normalizeIdsInCheckBetResponse(response.bets, isUnifiedFeedEnabled);

			dispatch({ type: BETS_CHECKED, payload: { bets: normalizedBets } });
			postCheckBetsCallback();
		} catch (err) {
			if (isCancelledError(err)) {
				return;
			}
			dispatch({ type: BETS_CHECKED, payload: { bets: [] } });
		}
	};

export const acceptBetsChanges = () => ({ type: BETS_CHANGES_ACCEPTED });

const normalizeIdsInPlaceBetResponse = (
	bets: Array<PlaceBetsResponseBet>,
	isUnifiedFeedEnabled: boolean
): Array<PlaceBetsResponseBet> => {
	if (!isUnifiedFeedEnabled) {
		return bets;
	}

	let normalizedBets = bets;

	if (normalizedBets.some(b => b.items)) {
		normalizedBets = normalizedBets.map(bet => ({
			...bet,
			items: bet.items.map(item => ({
				...item,
				selectionId: normalizeUnifiedId(item.selectionId)
			}))
		}));
	}

	if (normalizedBets.some(b => b.newOffer?.items)) {
		normalizedBets = normalizedBets.map(bet => ({
			...bet,
			newOffer: bet.newOffer
				? {
						...bet.newOffer,
						items: bet.newOffer?.items.map(item => ({
							...item,
							selectionId: normalizeUnifiedId(item.selectionId)
						}))
					}
				: undefined
		}));
	}

	if (normalizedBets.some(b => b.errors?.some(e => e.selectionIds))) {
		normalizedBets = normalizedBets.map(bet => ({
			...bet,
			errors: bet.errors.map(error => ({
				...error,
				selectionIds: error.selectionIds?.map(normalizeUnifiedId)
			}))
		}));
	}

	return normalizedBets;
};

export const placeBets =
	(
		bets: Array<BetslipBetWithData>
	): ThunkAction<
		| BetsPlacementStartedAction
		| BetsPlacementUpdateReceivedAction
		| BetsPlacementFailedAction
		| BetsPlacementUnknownAction
	> =>
	async (dispatch, getState) => {
		dispatch({
			type: BETS_PLACEMENT_STARTED,
			payload: {
				betRefs: bets.map(b => b.ref),
				// These we need for analytics:
				betType: bets[0]?.type,
				totalStake: bets.reduce((result, bet) => result + calculateBetWithdrawStake(bet), 0)
			}
		});
		debouncedCheckBets.cancel();

		try {
			const response = await apiPlaceBets({
				bets: bets.map(bet => {
					const { selectedBonus } = bet;
					return {
						ref: bet.ref,
						type: bet.type,
						stake: calculateBetWithdrawStake(bet),
						items: bet.items.map(item => ({
							eventId: item.event?.id || '',
							marketId: item.market?.id || '',
							marketTypeId: item.market?.marketType.externalId || '',
							selectionId: item.selection?.id || '',
							sportId: item.event?.externalSportId || '',
							leagueId: item.event?.leagueId || '',
							odds: item.finalOdds,
							points: item.selection?.points !== 0 ? item.selection?.points : undefined,
							isLive: item.event?.isLive || false
						})),
						combination: bet.combination,
						odds: bet.odds || 0,
						originalOdds: bet.originalOdds || 0,
						finalOdds: bet.finalOdds || 0,
						returns: bet.returns || 0,
						finalReturns: bet.finalReturns || 0,
						oddsBonusPercentage: 0,
						givenBonusTokenId: selectedBonus?.givenBonusTokenId,
						isFreeBet: selectedBonus?.bonusType === BetBonusType.FREE_BET,
						returnsTax: bet.returnsTax || 0,
						returnsTaxPercentage: bet.returnsTaxPercentage || 0,
						stakeTax: bet.stakeTax || 0,
						stakeTaxPercentage: bet.stakeTaxPercentage || 0
					};
				}),
				acceptAllOddChanges: bets[0].acceptAllOddChanges
			});

			const isUnifiedFeedEnabled = selectSportsbookUnifiedFeedEnabled(getState());
			const normalizedBets = normalizeIdsInPlaceBetResponse(response.bets, isUnifiedFeedEnabled);

			dispatch({
				type: BETS_PLACEMENT_UPDATE_RECEIVED,
				payload: { bets: normalizedBets }
			});

			// Automatic balance updates are flaky, so manually update the balance when we get the initial placement response
			dispatch(reloadBalance());

			if (normalizedBets.some(bet => bet.status === PlaceBetsResponseBetStatus.DELAYED)) {
				subscribeToPlacementUpdates({
					ref: SUBSCRIPTION_REFERENCE,
					onSubscribed: () => {},
					onMessage: message => {
						const normalizedUpdatedBets = normalizeIdsInPlaceBetResponse(
							message.bets,
							isUnifiedFeedEnabled
						);
						dispatch({
							type: BETS_PLACEMENT_UPDATE_RECEIVED,
							payload: { bets: normalizedUpdatedBets }
						});

						// Automatic balance updates are flaky, so manually update the balance when we get a placement update
						dispatch(reloadBalance());
					},
					// eslint-disable-next-line no-console
					onError: console.warn
				});
			}
		} catch (error) {
			const isUnknownError = error instanceof Error && error.message && error.message.startsWith('5');
			if (isUnknownError) {
				dispatch({ type: BETS_PLACEMENT_UNKNOWN, payload: { betRefs: bets.map(b => b.ref) } });
				// Automatic balance updates are flaky, so manually update the balance when something unexpected happens
				dispatch(reloadBalance());
			} else {
				dispatch({ type: BETS_PLACEMENT_FAILED, payload: { betRefs: bets.map(b => b.ref) } });
			}
		}
	};

// Accept new offer by replacing the bet with a new bet containing the odds from the new offer
export const acceptNewOffers =
	(betRefs: string[]): ThunkAction<NewOfferAcceptedAction | BetsPlacementFailedAction> =>
	(dispatch, getState) => {
		const bets = selectBets(getState());
		const validBets = bets.filter(
			b => betRefs.includes(b.ref) && b.placementStatus === BetPlacementStatus.NEW_OFFER_PENDING && b.newOffer
		);
		const validBetRefs = validBets.map(b => b.ref);

		if (validBets.length === 0) {
			return;
		}

		try {
			const newBetRefs = validBets.map(() => uniqueId(Date.now().toString()));
			dispatch({
				type: NEW_OFFER_ACCEPTED,
				payload: { betRefs: validBetRefs, newBetRefs }
			});

			const updatedBets = selectBetsWithData(getState());
			const updatedBetsToPlace = updatedBets.filter(b => newBetRefs.includes(b.ref));
			if (updatedBetsToPlace.length > 0) {
				dispatch(placeBets(updatedBetsToPlace));
			}
		} catch (err) {
			dispatch({
				type: BETS_PLACEMENT_FAILED,
				payload: { betRefs: validBetRefs }
			});
		}
	};

export const acceptAllNewOffers = (): ThunkAction => (dispatch, getState) => {
	// Find all bets that has a pending new offer.
	const bets = selectBets(getState());
	const newOfferBets = bets.filter(b => b.placementStatus === BetPlacementStatus.NEW_OFFER_PENDING && b.newOffer);
	// Dispatch actioncreator to accept ALL pending newoffers
	dispatch(acceptNewOffers(newOfferBets.map(b => b.ref)));
};

export const rejectNewOffers =
	(betRefs: string[]): ThunkAction<NewOfferRejectedAction> =>
	(dispatch, getState) => {
		const bets = selectBets(getState());
		const betsToReject = bets.filter(
			bet =>
				betRefs.includes(bet.ref) &&
				bet.placementStatus === BetPlacementStatus.NEW_OFFER_PENDING &&
				bet.newOffer
		);

		const rejectedBets = betsToReject.map(bet => ({
			...bet,
			placementStatus: BetPlacementStatus.NEW_OFFER_REJECTED
		}));

		if (rejectedBets.length > 0) {
			dispatch({
				type: NEW_OFFER_REJECTED,
				payload: { betRefs: rejectedBets.map(bet => bet.ref) }
			});
		}
	};

export const rejectAllNewOffers = (): ThunkAction => (dispatch, getState) => {
	// Find all bets that has a pending new offer.
	const bets = selectBets(getState());
	const newOfferBets = bets.filter(b => b.placementStatus === BetPlacementStatus.NEW_OFFER_PENDING && b.newOffer);
	// Dispatch actioncreator to reject ALL pending newoffers
	dispatch(rejectNewOffers(newOfferBets.map(b => b.ref)));
	unsubscribeFromPlacementUpdates(SUBSCRIPTION_REFERENCE);
};

export const dispose = (): DisposedAction => {
	unsubscribeFromPlacementUpdates(SUBSCRIPTION_REFERENCE);

	return { type: DISPOSED };
};

// Reducer
const handleBetsReplaced = (state: State, { payload: { bets } }: BetsReplacedAction): State => {
	return { ...state, bets };
};

const handleStakeChanged = (state: State, { payload: { ref, stake } }: StakeChangedAction): State => {
	return {
		...state,
		bets: state.bets.map(bet => (bet.ref === ref ? { ...bet, stake } : bet))
	};
};

const handleToggleAcceptAllOddsChanges = (state: State): State => {
	return {
		...state,
		bets: state.bets.map(bet => {
			return { ...bet, acceptAllOddChanges: !bet.acceptAllOddChanges };
		})
	};
};

const handleSelectedBonusIdChanged = (
	state: State,
	{ payload: { ref, bonusId } }: SelectedBonusIdChangedAction
): State => {
	return {
		...state,
		bets: state.bets.map(bet => {
			if (bet.ref !== ref) {
				return bet;
			}

			// If we unset bonus from bet.
			if (bonusId === undefined) {
				return {
					...bet,
					selectedBonus: undefined,
					stake: bet.stake,
					finalOdds: calculateBetFinalOdds(bet.items)
				};
			}

			const bonus = bet.bonuses.find(b => b.givenBonusTokenId === bonusId);
			if (!bonus) {
				return bet;
			}

			// If bonus is FreeBet, we need to set freebet amount to stake.
			let { stake } = bet;
			if (bonus?.bonusType === BetBonusType.FREE_BET) {
				stake = bonus.freeBetTokenAmount;
			}
			return { ...bet, selectedBonus: bonus, stake, finalOdds: calculateBetFinalOdds(bet.items, bonus) };
		})
	};
};

const handleBetsChecked = (state: State, { payload: { bets: checkedBets } }: BetsCheckedAction): State => {
	return {
		...state,
		bets: state.bets.map(bet => {
			const checkedBet = checkedBets.find(cb => cb.ref === bet.ref);
			if (!checkedBet) {
				return bet;
			}

			const betErrors = checkedBet.errors
				.filter(error => error.betRef === bet.ref)
				.map(bettingApiErrorToBetslipBetError);

			const betWarnings =
				checkedBet.warnings
					?.filter(warning => warning.betRef === bet.ref)
					.map(bettingApiWarningToBetslipBetWarning) || [];

			const hasUnavailableError = betErrors.some(e => isUnavailableError(e));
			return {
				...bet,
				isChecked: true,
				originalOdds: checkedBet.originalOdds,
				odds: checkedBet.odds,
				finalOdds: checkedBet.finalOdds,
				returns: checkedBet.returns,
				finalReturns: checkedBet.finalReturns,
				minStake: checkedBet.minStake,
				maxStake: checkedBet.maxStake,
				bonuses: checkedBet.bonusTokens || [],
				returnsTax: checkedBet.returnsTax,
				returnsTaxPercentage: checkedBet.returnsTaxPercentage,
				stakeTax: checkedBet.stakeTax,
				stakeTaxPercentage: checkedBet.stakeTaxPercentage,
				errors: betErrors,
				warnings: betWarnings,
				// If bet has unavailable error we want to unset selectedBonus from it.
				selectedBonus: hasUnavailableError ? undefined : bet.selectedBonus
			} as BetslipBet;
		})
	};
};

const handleBetsChangesAccepted = (state: State): State => ({
	...state,
	bets: state.bets.map(bet => {
		// Return bet without error with type ODDS_UPDATED
		const errorsWithoutOddsUpdated = bet.errors.filter(e => e.type !== BetslipBetErrorType.ODDS_UPDATED);
		return { ...bet, errors: errorsWithoutOddsUpdated };
	})
});

const handleBetsPlacementStarted = (state: State, { payload: { betRefs } }: BetsPlacementStartedAction): State => ({
	...state,
	bets: state.bets.map(bet =>
		betRefs.some(betRef => betRef === bet.ref) ? { ...bet, placementStatus: BetPlacementStatus.PLACING } : bet
	)
});

const handleBetsPlacementUpdatesReceived = (
	state: State,
	{ payload: { bets: updatedBets } }: BetsPlacementUpdateReceivedAction
): State => {
	return {
		...state,
		bets: state.bets.map(bet => {
			const updatedBet = updatedBets.find(pb => pb.ref === bet.ref);
			if (!updatedBet) {
				return bet;
			}

			if (updatedBet.status === PlaceBetsResponseBetStatus.PLACED) {
				return {
					...bet,
					odds: updatedBet.odds,
					returns: updatedBet.returns,
					finalOdds: updatedBet.finalOdds,
					finalReturns: updatedBet.finalReturns,
					returnsTax: updatedBet.returnsTax,
					returnsTaxPercentage: updatedBet.returnsTaxPercentage,
					createdDate: updatedBet.createdDate || '',
					errors: [],
					placementStatus: BetPlacementStatus.PLACED,
					items: bet.items.map(betItem => ({
						...betItem,
						finalOdds:
							updatedBet.items.find(placedItem => betItem.selectionId === placedItem.selectionId)?.odds ||
							0
					}))
				};
			}

			const betErrors = updatedBet.errors
				.map(bettingApiErrorToBetslipBetError)
				.filter(error => error.betRef === bet.ref);

			const betNewOffer = updatedBet.newOffer;
			const isOnlyOddsUpdatedErrors = betErrors.every(error => error.type === BetslipBetErrorType.ODDS_UPDATED);

			const isNewOffer = betNewOffer && isOnlyOddsUpdatedErrors;

			if (isNewOffer) {
				// Update bet with final odds and final returns we got from the new offer
				return {
					...bet,
					errors: [],
					placementStatus: BetPlacementStatus.NEW_OFFER_PENDING,
					newOffer: betNewOffer,
					odds: betNewOffer.odds,
					returns: betNewOffer.returns,
					finalOdds: betNewOffer.finalOdds,
					finalReturns: betNewOffer.finalReturns,
					returnsTax: betNewOffer.returnsTax,
					returnsTaxPercentage: betNewOffer.returnsTaxPercentage,
					items: bet.items.map(betItem => {
						const newOfferItem = betNewOffer.items.find(
							({ selectionId }) => betItem.selectionId === selectionId
						);
						return {
							...betItem,
							finalOdds: newOfferItem?.odds || betItem.finalOdds
						};
					})
				};
			}

			if (updatedBet.status === PlaceBetsResponseBetStatus.ERROR) {
				// Odds updated errors are only used for new offer process above
				const betErrorsWithoutOddsUpdated = betErrors.filter(
					error => error.type !== BetslipBetErrorType.ODDS_UPDATED
				);
				return { ...bet, placementStatus: BetPlacementStatus.DECLINED, errors: betErrorsWithoutOddsUpdated };
			}

			return bet;
		})
	};
};

const handleBetsPlacementFailed = (state: State, { payload: { betRefs } }: BetsPlacementFailedAction): State => ({
	...state,
	bets: state.bets.map(bet =>
		betRefs.some(betRef => betRef === bet.ref) ? { ...bet, placementStatus: BetPlacementStatus.DECLINED } : bet
	)
});

const handleBetsPlacementUnknown = (state: State, { payload: { betRefs } }: BetsPlacementUnknownAction): State => ({
	...state,
	bets: state.bets.map(bet =>
		betRefs.some(betRef => betRef === bet.ref) ? { ...bet, placementStatus: BetPlacementStatus.UNKNOWN } : bet
	)
});

const handleNewOfferAccepted = (state: State, { payload: { betRefs, newBetRefs } }: NewOfferAcceptedAction): State => {
	const updatedBets = state.bets.map(bet => {
		const index = betRefs.indexOf(bet.ref);
		if (index !== -1) {
			return {
				...bet,
				ref: newBetRefs[index],
				newOffer: undefined
			};
		}
		return bet;
	});

	return {
		...state,
		bets: updatedBets
	};
};

const handleNewOfferRejected = (state: State, { payload: { betRefs } }: NewOfferRejectedAction): State => {
	const updatedBets = state.bets.map(bet => {
		if (betRefs.includes(bet.ref)) {
			return { ...bet, placementStatus: BetPlacementStatus.NEW_OFFER_REJECTED };
		}
		return bet;
	});

	return {
		...state,
		bets: updatedBets
	};
};
const handleDisposed = () => initialState;

const reducer = createReducer<State>(initialState, {
	[BETS_REPLACED]: handleBetsReplaced,
	[STAKE_CHANGED]: handleStakeChanged,
	[TOGGLE_ACCEPT_ALL_ODDS_CHANGES]: handleToggleAcceptAllOddsChanges,
	[SELECTED_BONUS_ID_CHANGED]: handleSelectedBonusIdChanged,
	[BETS_CHECKED]: handleBetsChecked,
	[DISPOSED]: handleDisposed,
	[BETS_CHANGES_ACCEPTED]: handleBetsChangesAccepted,
	[BETS_PLACEMENT_STARTED]: handleBetsPlacementStarted,
	[BETS_PLACEMENT_UPDATE_RECEIVED]: handleBetsPlacementUpdatesReceived,
	[BETS_PLACEMENT_FAILED]: handleBetsPlacementFailed,
	[BETS_PLACEMENT_UNKNOWN]: handleBetsPlacementUnknown,
	[NEW_OFFER_ACCEPTED]: handleNewOfferAccepted,
	[NEW_OFFER_REJECTED]: handleNewOfferRejected
});

export default reducer;
